import {
  type AdultVaccineInfo,
  getAdultVaccineInfo,
} from '#src/cdc/vaccines/adult-vaccines.ts'
import type { VaccineFamily } from '#src/cdc/vaccines/cvx-mapping.ts'
import type { VisitNote } from '#src/models/elation/visitNotes.ts'

interface VaccineFamilies {
  [immunizationType: string]: VaccineFamily
}

/**
 * Maps Elation immunization declination patterns to standardized vaccine families
 * Includes multiple variations and aliases to handle different formatting
 */
const ELATION_IMMUNIZATION_TYPE_TO_VACCINE_FAMILY: VaccineFamilies = {
  /* COVID-19 */
  'COVID-19': 'COVID-19',
  COVID: 'COVID-19',
  COVID19: 'COVID-19',
  'SARS-CoV-2': 'COVID-19',

  /* Hepatitis A */
  'Hep A': 'Hepatitis A',
  HepA: 'Hepatitis A',
  'Hepatitis A': 'Hepatitis A',

  /* Hepatitis B */
  'Hep B': 'Hepatitis B',
  HepB: 'Hepatitis B',
  'Hepatitis B': 'Hepatitis B',

  /* Hib */
  Hib: 'Hib',
  HIB: 'Hib',
  Haemophilus: 'Hib',
  'H. influenzae': 'Hib',

  /* HPV */
  HPV: 'HPV',
  Gardasil: 'HPV',
  HPV9: 'HPV',

  /* Influenza */
  Influenza: 'Influenza',
  Flu: 'Influenza',

  /* MMR */
  MMR: 'MMR',
  Measles: 'MMR',
  Mumps: 'MMR',
  Rubella: 'MMR',
  MMRV: 'MMR',

  /* Meningococcal ACWY */
  'Meningococcal ACWY': 'Meningococcal (ACWY)',
  MenACWY: 'Meningococcal (ACWY)',
  Meningococcal: 'Meningococcal (ACWY)',
  Menactra: 'Meningococcal (ACWY)',
  Menveo: 'Meningococcal (ACWY)',

  /* Meningococcal B */
  'Meningococcal B': 'Meningococcal (B)',
  MenB: 'Meningococcal (B)',
  Bexsero: 'Meningococcal (B)',
  Trumenba: 'Meningococcal (B)',

  /* Mpox */
  Mpox: 'Mpox',
  Monkeypox: 'Mpox',
  Jynneos: 'Mpox',

  /* Pneumococcal */
  Pneumococcal: 'Pneumococcal',
  PCV: 'Pneumococcal',
  PCV13: 'Pneumococcal',
  PCV15: 'Pneumococcal',
  PCV20: 'Pneumococcal',
  PCV21: 'Pneumococcal',
  PPSV23: 'Pneumococcal',
  PPSV: 'Pneumococcal',
  Prevnar: 'Pneumococcal',
  Pneumovax: 'Pneumococcal',

  /* RSV */
  RSV: 'RSV',

  /* Tetanus/Diphtheria */
  Td: 'Tetanus/Diphtheria (Td)',
  TD: 'Tetanus/Diphtheria (Td)',
  'Tetanus/Diphtheria': 'Tetanus/Diphtheria (Td)',

  /* Tdap */
  Tdap: 'Tdap',
  TDAP: 'Tdap',
  Boostrix: 'Tdap',
  Adacel: 'Tdap',

  /* Varicella */
  Varicella: 'Varicella',
  Chickenpox: 'Varicella',
  VAR: 'Varicella',

  /* Zoster (Shingles) */
  Zoster: 'Zoster (Shingles)',
  Shingles: 'Zoster (Shingles)',
  ZOS: 'Zoster (Shingles)',
  Shingrix: 'Zoster (Shingles)',
  Zostavax: 'Zoster (Shingles)',
}

/**
 * Regular expression pattern to match immunization declination text
 * Generic pattern: "Patient declined [TYPE] immunization" or "Patient declined [TYPE] vaccine"
 */
const DECLINATION_PATTERN =
  /Patient declined\s+(.+?)\s+(?:immunization|vaccine)/i

/**
 * Normalizes the immunization type for flexible matching
 */
function normalizeImmunizationType(type: string): string {
  return type
    .trim()
    .replace(/\s*\/\s*/g, '/') /* Normalize slashes */
    .replace(/,\s*/g, ', ') /* Normalize commas */
}

/**
 * Extracts and maps the immunization type from a declination text
 */
function extractImmunizationType(text: string): VaccineFamily | null {
  const match = text.match(DECLINATION_PATTERN)
  if (!match?.[1]) {
    return null
  }

  const rawType = match[1].trim()
  const normalized = normalizeImmunizationType(rawType)

  /* Try exact match first */
  let vaccineFamily = ELATION_IMMUNIZATION_TYPE_TO_VACCINE_FAMILY[normalized]
  if (vaccineFamily) {
    return vaccineFamily
  }

  /* Try case-insensitive match */
  const lowerType = normalized.toLowerCase()
  for (const [key, family] of Object.entries(
    ELATION_IMMUNIZATION_TYPE_TO_VACCINE_FAMILY
  )) {
    if (key.toLowerCase() === lowerType) {
      return family
    }
  }

  /* Handle compound types (e.g., "PCV15/PCV20", "PCV15, PCV20") */
  const parts = normalized.split(/[,/]/).map((p) => p.trim())
  if (parts.length > 1) {
    /* Try to match first part */
    vaccineFamily = ELATION_IMMUNIZATION_TYPE_TO_VACCINE_FAMILY[parts[0]]
    if (vaccineFamily) {
      return vaccineFamily
    }
    /* Try case-insensitive match on first part */
    const lowerPart = parts[0].toLowerCase()
    for (const [key, family] of Object.entries(
      ELATION_IMMUNIZATION_TYPE_TO_VACCINE_FAMILY
    )) {
      if (key.toLowerCase() === lowerPart) {
        return family
      }
    }
  }

  return null
}

/**
 * Represents an immunization declination found in a visit note
 */
export interface ImmunizationDeclination {
  /**
   * The vaccine family (standardized)
   */
  vaccineFamily: VaccineFamily

  /**
   * The original text pattern that was matched
   */
  originalText: string
}

/**
 * Extracts immunization declinations from a visit note
 *
 * Scans all bullets in the visit note (including children) for declination patterns
 * and returns a deduplicated list of declined vaccines.
 *
 * @param visitNote - The Elation visit note to scan
 * @returns Array of unique immunization declinations
 */
export function extractImmunizationDeclinations(
  visitNote: VisitNote
): ImmunizationDeclination[] {
  const declinations: ImmunizationDeclination[] = []
  const seen = new Set<VaccineFamily>()

  /* Helper to process a single bullet */
  const processBullet = (bulletText: string): void => {
    /* Check if text matches declination pattern */
    if (!DECLINATION_PATTERN.test(bulletText)) {
      return
    }

    /* Extract and map to vaccine family */
    const vaccineFamily = extractImmunizationType(bulletText)
    if (!vaccineFamily) {
      return
    }

    /* Add to results if not already seen */
    if (!seen.has(vaccineFamily)) {
      seen.add(vaccineFamily)
      declinations.push({
        vaccineFamily,
        originalText: bulletText,
      })
    }
  }

  /* Process all bullets including nested children */
  for (const bullet of visitNote.bullets) {
    if (bullet.text) {
      processBullet(bullet.text)
    }

    /* Process child bullets */
    if (bullet.children) {
      for (const child of bullet.children) {
        if (child.text) {
          processBullet(child.text)
        }
      }
    }
  }

  return declinations
}

/**
 * Gets vaccine info for a vaccine family
 *
 * @param family - The vaccine family
 * @returns Vaccine info including CVX codes, or undefined if not found
 */
export function getVaccineInfoForFamily(
  family: VaccineFamily
): AdultVaccineInfo | undefined {
  return getAdultVaccineInfo(family)
}
